import { useId, type ReactNode } from 'react';
import { isRtl } from '@motorcity/shared';
import { useEditLocale } from './EditLocale';
import { Select, Switch } from './ui/primitives';

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  );
}

export interface LocalizedValue {
  en: string;
  ar: string;
}

/**
 * A translatable field, following whichever language the page-level switch is
 * on. Arabic flips the control to RTL so editors can judge what they are
 * typing, and a badge marks the field when the other language is still blank.
 */
export function LocalizedInput({
  label,
  value,
  onChange,
  multiline = false,
  rows = 4,
  required = false,
  error,
  hint,
  autoFocus,
}: {
  label: string;
  value: LocalizedValue | undefined;
  onChange: (next: LocalizedValue) => void;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  error?: string;
  hint?: string;
  autoFocus?: boolean;
}) {
  const { locale } = useEditLocale();
  const id = useId();
  const current: LocalizedValue = { en: value?.en ?? '', ar: value?.ar ?? '' };
  const rtl = isRtl(locale);
  const other = locale === 'en' ? 'ar' : 'en';
  const otherBlank = !current[other].trim();

  const shared = {
    id,
    value: current[locale],
    dir: rtl ? ('rtl' as const) : ('ltr' as const),
    lang: locale,
    className: rtl ? 'input rtl' : 'input',
    'aria-invalid': error ? true : undefined,
    autoFocus,
    onChange: (e: { target: { value: string } }) =>
      onChange({ ...current, [locale]: e.target.value }),
  };

  return (
    <div className="field">
      <div className="field-header">
        <label className="field-label" htmlFor={id}>
          {label}
          {required ? <span className="required" aria-hidden="true"> *</span> : null}
        </label>
        {otherBlank ? (
          <span className="pill warn" title={`No ${other === 'ar' ? 'Arabic' : 'English'} text yet`}>
            {other.toUpperCase()} missing
          </span>
        ) : null}
      </div>
      {multiline ? <textarea {...shared} rows={rows} /> : <input {...shared} type="text" />}
      {hint && !error ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
      {locale === 'ar' && !current.ar.trim() ? (
        <span className="field-hint">Left blank — the app falls back to English.</span>
      ) : null}
    </div>
  );
}

const CURRENCIES = ['BHD', 'SAR', 'AED', 'USD'] as const;

export interface PriceValue {
  amount: number;
  currency: (typeof CURRENCIES)[number];
  display?: LocalizedValue;
}

export function PriceInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PriceValue | undefined;
  onChange: (next: PriceValue | undefined) => void;
}) {
  const enabled = value !== undefined;
  const current: PriceValue = value ?? { amount: 0, currency: 'BHD' };

  return (
    <div className="field">
      <Switch
        checked={enabled}
        onChange={(on) => onChange(on ? current : undefined)}
        label={label}
        hint={enabled ? undefined : 'Hidden in the app until switched on.'}
      />
      {enabled ? (
        <div className="field-body">
          <div className="input-group">
            <span className="input-affix">{current.currency}</span>
            <input
              className="input"
              type="number"
              min={0}
              step="0.001"
              inputMode="decimal"
              aria-label={`${label} amount`}
              value={current.amount}
              onChange={(e) => onChange({ ...current, amount: Number(e.target.value) })}
            />
            <Select
              value={current.currency}
              ariaLabel="Currency"
              onChange={(next) => onChange({ ...current, currency: next as PriceValue['currency'] })}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface EmiValue {
  monthly: number;
  currency: (typeof CURRENCIES)[number];
  months?: number;
  apr?: number;
  display?: LocalizedValue;
}

export function EmiInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: EmiValue | undefined;
  onChange: (next: EmiValue | undefined) => void;
}) {
  const enabled = value !== undefined;
  const current: EmiValue = value ?? { monthly: 0, currency: 'BHD', months: 60 };

  return (
    <div className="field">
      <Switch
        checked={enabled}
        onChange={(on) => onChange(on ? current : undefined)}
        label={label}
        hint={enabled ? undefined : 'Hidden in the app until switched on.'}
      />
      {enabled ? (
        <div className="field-body">
          <div className="grid-3">
            <label className="sub-field">
              <span className="sub-label">Per month</span>
              <div className="input-group">
                <span className="input-affix">{current.currency}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  step="0.001"
                  inputMode="decimal"
                  value={current.monthly}
                  onChange={(e) => onChange({ ...current, monthly: Number(e.target.value) })}
                />
              </div>
            </label>
            <label className="sub-field">
              <span className="sub-label">Term</span>
              <div className="input-group">
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={120}
                  inputMode="numeric"
                  value={current.months ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...current,
                      months: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
                <span className="input-affix suffix">months</span>
              </div>
            </label>
            <label className="sub-field">
              <span className="sub-label">APR</span>
              <div className="input-group">
                <input
                  className="input"
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={current.apr ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...current,
                      apr: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
                <span className="input-affix suffix">%</span>
              </div>
            </label>
          </div>
          <div className="currency-row">
            <span className="sub-label">Currency</span>
            <Select
              value={current.currency}
              compact
              ariaLabel="EMI currency"
              onChange={(next) => onChange({ ...current, currency: next as EmiValue['currency'] })}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Publication gate. Presented as two explicit choices rather than a toggle,
 * because "draft vs live" is a decision with consequences and deserves its
 * consequence spelled out.
 */
export function StatusSelect({
  value,
  onChange,
  warning,
}: {
  value: 'draft' | 'live';
  onChange: (next: 'draft' | 'live') => void;
  /** Rendered under the choices when Live is selected but the record has gaps. */
  warning?: ReactNode;
}) {
  return (
    <div className="field">
      <span className="field-label">Visibility</span>
      <div className="choice-group">
        {(
          [
            {
              key: 'draft' as const,
              title: 'Draft',
              body: 'Hidden from the app, even after publishing.',
            },
            {
              key: 'live' as const,
              title: 'Live',
              body: 'Included the next time someone publishes.',
            },
          ]
        ).map((option) => (
          <label
            key={option.key}
            className={value === option.key ? 'choice selected' : 'choice'}
          >
            <input
              type="radio"
              name="status"
              className="choice-input"
              checked={value === option.key}
              onChange={() => onChange(option.key)}
            />
            <span className={`status-dot ${option.key}`} aria-hidden="true" />
            <span className="choice-text">
              <span className="choice-title">{option.title}</span>
              <span className="choice-body">{option.body}</span>
            </span>
          </label>
        ))}
      </div>
      {value === 'live' ? warning : null}
    </div>
  );
}
